import {useMutation, useQueryClient} from "@tanstack/react-query";
import {message} from "antd";
import {AxiosError} from "axios";
import {
  StripeRepository,
  StripeOnboardResult,
} from "../data/repositories/stripe.repository";
import {V2StripeOnboardRequest} from "../data/mappers/bank-account.mapper.types";
import {BANK_ACCOUNT_QUERY_KEYS} from "./useBankAccounts";

const stripeRepository = new StripeRepository();

// ==================== Domain Hooks ====================

/**
 * Hook for onboarding a new Stripe account.
 * Returns BankAccount with loginUrl.
 */
export const useOnboardStripe = () => {
  const queryClient = useQueryClient();

  return useMutation<
    StripeOnboardResult,
    AxiosError<{message?: string}>,
    V2StripeOnboardRequest
  >({
    mutationFn: (params) => stripeRepository.onboardStripe(params),
    onError: (error) => {
      message.error(error.message ?? "Failed to connect to Stripe");
    },
    onSuccess: (result) => {
      message.success(
        "Your bank account has been updated successfully. Please complete your registration."
      );
      if (result.loginUrl) {
        setTimeout(() => {
          window.open(result.loginUrl!);
        }, 0);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: BANK_ACCOUNT_QUERY_KEYS.connected()});
      queryClient.invalidateQueries({queryKey: ["connected_bank"]});
      queryClient.invalidateQueries({queryKey: ["dashboard_status"]});
    },
  });
};

/**
 * Hook for initiating Stripe account update (v2 two-step flow).
 * Step 1: Sends OTP to user's registered email.
 */
export const useInitiateStripeUpdate = () => {
  return useMutation<void, AxiosError<{message?: string}>, {countryCode: string}>({
    mutationFn: ({countryCode}) => stripeRepository.initiateStripeUpdate(countryCode),
    onError: (error) => {
      message.error(error.message ?? "Failed to initiate Stripe update");
    },
    onSuccess: () => {
      message.success("OTP sent to your registered email");
    },
  });
};

/**
 * Hook for completing Stripe account update (v2 two-step flow).
 * Step 2: Called after user enters OTP from initiateStripeUpdate.
 */
export const useCompleteStripeUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation<
    StripeOnboardResult,
    AxiosError<{message?: string}>,
    {otpCode: string}
  >({
    mutationFn: ({otpCode}) => stripeRepository.completeStripeUpdate(otpCode),
    onError: (error) => {
      message.error(error.message ?? "Failed to complete Stripe update");
    },
    onSuccess: (result) => {
      message.success("Stripe account updated successfully");
      if (result.loginUrl) {
        message.info("Redirecting to Stripe...", 1, () => {
          setTimeout(() => {
            window.open(result.loginUrl!);
          }, 0);
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: BANK_ACCOUNT_QUERY_KEYS.connected()});
      queryClient.invalidateQueries({queryKey: ["connected_bank"]});
      queryClient.invalidateQueries({queryKey: ["dashboard_status"]});
    },
  });
};

/**
 * Hook for disconnecting any payout account (v2 only).
 * Disconnects Paystack, Flutterwave, or Stripe accounts.
 */
export const useDisconnectBankAccount = () => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError<{message?: string}>, void>({
    mutationFn: () => stripeRepository.disconnectBankAccount(),
    onError: (error) => {
      message.error(error.message ?? "Failed to disconnect bank account");
    },
    onSuccess: () => {
      message.success("Bank account disconnected successfully");
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: BANK_ACCOUNT_QUERY_KEYS.connected()});
      queryClient.invalidateQueries({queryKey: ["connected_bank"]});
      queryClient.invalidateQueries({queryKey: ["dashboard_status"]});
    },
  });
};

/**
 * Hook for completing Stripe registration (inactive accounts).
 * Called after user returns from Stripe Connect onboarding.
 * Redirects to Stripe or refetches bank account if already verified.
 */
export const useCompleteStripeRegistration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => stripeRepository.completeStripeRegistration(),
    onError: (error: AxiosError<{message?: string}>) => {
      message.error(error.message ?? "Failed to complete Stripe registration");
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({queryKey: BANK_ACCOUNT_QUERY_KEYS.connected()});
      queryClient.invalidateQueries({queryKey: ["connected_bank"]});
      queryClient.invalidateQueries({queryKey: ["dashboard_status"]});
      if (result.loginUrl) {
        message.info("Redirecting to Stripe...", 1, () => {
          setTimeout(() => {
            window.open(result.loginUrl!);
          }, 0);
        });
      }
    },
  });
};

/**
 * Hook for getting Stripe login link (active accounts).
 * Redirects to Stripe dashboard.
 * Note: In v2, use loginUrl from bank account response instead.
 */
export const useLoginToStripe = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => stripeRepository.loginToStripe(),
    onError: (error: AxiosError<{message?: string}>) => {
      message.error(error.message ?? "Failed to get Stripe login link");
    },
    onSuccess: (data) => {
      if (data.link) {
        message.info("Redirecting to stripe....", 1, () => {
          setTimeout(() => {
            window.open(data.link);
          }, 0);
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: BANK_ACCOUNT_QUERY_KEYS.connected()});
      queryClient.invalidateQueries({queryKey: ["connected_bank"]});
      queryClient.invalidateQueries({queryKey: ["dashboard_status"]});
    },
  });
};

// ==================== Legacy-Compatible Hooks ====================

/**
 * Payload type for legacy useConnectToStripe hook.
 */
export type TConnectToStripePayload = {
  countryCode: string;
  countryName: string;
  currencyCode: string;
};

/**
 * Legacy-compatible hook for connecting to Stripe.
 *
 * @deprecated Use useOnboardStripe for new code.
 */
export const useConnectToStripe = () => {
  const queryClient = useQueryClient();

  return useMutation<
    StripeOnboardResult,
    AxiosError<{message?: string}>,
    TConnectToStripePayload
  >({
    mutationFn: (payload) => stripeRepository.onboardStripe(payload),
    onError: (error) => {
      message.error(error.message ?? "Failed to connect to Stripe");
    },
    onSuccess: (result) => {
      message.success(
        "Your bank account has been updated successfully. Please complete your registration."
      );
      if (result.loginUrl) {
        setTimeout(() => {
          window.open(result.loginUrl!);
        }, 0);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: BANK_ACCOUNT_QUERY_KEYS.connected()});
      queryClient.invalidateQueries({queryKey: ["connected_bank"]});
      queryClient.invalidateQueries({queryKey: ["dashboard_status"]});
    },
  });
};

// ==================== Composed Hook ====================

export interface UseStripeActionsOptions {
  /** Refetch connected bank account after successful complete registration when already verified */
  refetchConnectedBank?: () => void;
}

/**
 * Composes Stripe action mutations with success/error handling.
 * Returns handlers suitable for BankConnectionBanner.
 */
export const useStripeActions = (_options?: UseStripeActionsOptions) => {
  const completeRegistrationMutation = useCompleteStripeRegistration();
  const loginToStripeMutation = useLoginToStripe();
  const disconnectMutation = useDisconnectBankAccount();

  const completeRegistration = () => {
    completeRegistrationMutation.mutate(undefined);
  };

  const loginToStripe = () => {
    loginToStripeMutation.mutate(undefined);
  };

  const disconnect = () => {
    disconnectMutation.mutate(undefined);
  };

  const isLoading =
    completeRegistrationMutation.isPending ||
    loginToStripeMutation.isPending ||
    disconnectMutation.isPending;

  return {
    completeRegistration,
    loginToStripe,
    disconnect,
    isLoading,
    isDisconnectAvailable: true,
  };
};
